import styled from '@emotion/styled';
import Calendar from '@engineering/icons/calendar';

type ButtonProps = {
  $size: 'small' | 'default';
  $invalid?: boolean;
};

type ValueProps = {
  $dateDisplay: 'short' | 'default';
};

export const ButtonWrapper = styled.div`
  position: relative;
  grid-area: date-picker;
`;

export const Button = styled.button<ButtonProps>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0;
  padding: ${({ $size }) =>
    $size === 'small' ? '1rem 0.75rem 0.25rem' : '0.625rem 1rem'};
  border: ${({ theme, $invalid }) =>
    `1px solid ${$invalid ? theme.colors.accent.red100 : theme.colors.neutral.grey80}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  width: 100%;
  height: ${({ $size }) => ($size === 'small' ? '2.5rem' : '3.125rem')};
  font-size: ${({ $size }) => ($size === 'small' ? '0.75rem' : '1rem')};
  font-family: inherit;
  line-height: ${({ $size }) => ($size === 'small' ? 1 : 1.5)};
  text-transform: capitalize;
  text-align: left;
  color: ${({ theme }) => theme.colors.neutral.grey160};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  cursor: pointer;

  &:hover,
  &:focus {
    border-color: ${({ theme, $invalid }) =>
      $invalid ? theme.colors.accent.red100 : theme.colors.neutral.grey140};
  }

  &:focus-visible {
    outline: 0.125rem dashed ${({ theme }) => theme.colors.brand.orange100};
    outline-offset: 0.25rem;
  }

  &::after {
    content: attr(title);
    position: absolute;
    top: ${({ $size }) => ($size === 'small' ? '1px' : '-0.5625rem')};
    left: 0.75rem;
    z-index: 2;
    padding-inline: ${({ $size }) => ($size === 'small' ? '' : '0.25rem')};
    color: ${({ theme, $invalid, $size }) => {
      if ($invalid) {
        return theme.colors.accent.red100;
      }
      if ($size === 'small') {
        return theme.colors.neutral.grey120;
      }
      return theme.colors.neutral.grey140;
    }};
    background-color: ${({ theme, $size }) =>
      $size === 'small' ? 'transparent' : theme.colors.neutral.white100};
    font-size: 0.75rem;
    line-height: 1.5;
    overflow: hidden;
    pointer-events: none;
    text-transform: none;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CalendarIcon = styled(Calendar)`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  fill: currentcolor;
  pointer-events: none;
`;

export const Value = styled.span<ValueProps>`
  flex-grow: ${({ $dateDisplay }) => ($dateDisplay === 'short' ? '1' : '')};
  display: inline-grid;
  grid-template-columns: ${({ $dateDisplay }) =>
    $dateDisplay === 'short'
      ? '3.5fr max-content 4fr'
      : 'repeat(3, max-content)'};
  align-items: center;
  justify-content: ${({ $dateDisplay }) =>
    $dateDisplay === 'short' ? 'space-between' : ''};
  gap: ${({ $dateDisplay }) => ($dateDisplay === 'short' ? '1em' : '0.25em')};

  time:last-of-type {
    text-align: ${({ $dateDisplay }) =>
      $dateDisplay === 'short' ? 'center' : ''};
  }
`;
